import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Avatar, Rating, CircularProgress } from '@mui/material';
import { LocationOn, WorkOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from '../../api/userApi';
import SkillLabel from '../Common/SkillLabel';
import TranslatedText from '../Common/TranslatedText';
import { mediaVariantUrl } from '../../utils/mediaUrl';

// Palette aligned with Most Popular + Featured Services sections
const COLORS = {
  sectionBg: '#FAF8F5',
  cardBg: '#FFFFFF',
  border: '#E8DDD4',
  heading: '#2D2D2D',
  eyebrow: '#9A6845',
  name: '#1A1A1A',
  accent: '#C97B3A',
  accentHover: '#B56A2E',
  desc: '#888888',
  subtitle: '#9A6845',
  label: '#6B3F22',
  imageBg: '#F0E8DF',
  badgeNowBg: '#E8F5E9',
  badgeNowText: '#2E7D32',
  badgeTodayBg: '#FFF3E0',
  badgeTodayText: '#E65100',
  shadow: 'rgba(0,0,0,0.08)',
  shadowHover: 'rgba(0,0,0,0.16)',
  scrollbar: '#D8C7B8',
  scrollbarHover: '#9A6845',
  outlineBorder: '#9A6845',
  outlineText: '#7A4B2A',
  outlineHoverBg: 'rgba(122,75,42,0.06)',
};

const CARD_W = 200;
const DISPLAY_LIMIT = 12;

const formatPrice = (price, unit) => {
  if (price == null || Number.isNaN(Number(price))) return 'Rate on request';
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(price));
  if (!unit) return formatted;
  return `${formatted}/${unit === 'hour' || unit === 'hourly' ? 'hr' : unit === 'person' ? 'person' : 'day'}`;
};

const getWorkerLocation = (worker) => {
  const addr = worker?.user?.address;
  if (!addr) return 'Location not set';
  if (addr.village) return addr.village;
  if (addr.city && addr.state) return `${addr.city}, ${addr.state}`;
  if (addr.city) return addr.city;
  if (addr.area) return addr.area;
  return 'Location not set';
};

const getMaxExperienceYears = (skills) => {
  if (!Array.isArray(skills) || skills.length === 0) return null;
  const years = skills
    .map((s) => Number(s?.experienceYears))
    .filter((n) => !Number.isNaN(n) && n >= 0);
  if (years.length === 0) return null;
  return Math.max(...years);
};

const mapWorkerToCard = (worker) => {
  const skills = Array.isArray(worker.skills)
    ? worker.skills.map((s) => s?.name).filter(Boolean)
    : [];
  const primarySkill = Array.isArray(worker.skills) ? worker.skills[0] : null;
  const pricing = primarySkill?.pricingModel || {};
  const expYears = getMaxExperienceYears(worker.skills);

  return {
    id: worker._id,
    name: worker.user?.fullName?.trim() || 'Worker',
    skills: skills.length > 0 ? skills : ['Service'],
    rating: Number(worker.averageRating) || 0,
    experience: expYears != null ? `${expYears} yr${expYears === 1 ? '' : 's'}` : null,
    location: getWorkerLocation(worker),
    price: pricing.rate,
    priceUnit: pricing.type || 'daily',
    availability: worker.isOnline ? 'now' : 'today',
    photo: mediaVariantUrl(worker.user?.profilePicture, 'thumb') || null,
  };
};

const isValidWorker = (worker) =>
  worker &&
  worker._id &&
  worker.user &&
  worker.user.fullName &&
  worker.user.fullName.trim() !== '' &&
  worker.user.fullName !== 'Unknown Worker' &&
  Array.isArray(worker.skills) &&
  worker.skills.length > 0;

const AvailableWorkersSection = ({ location = '' }) => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchWorkers = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const buildParams = (withAvailability) => {
          const params = new URLSearchParams();
          if (withAvailability) params.append('availability', today);
          params.append('sortBy', 'rating');
          params.append('sortOrder', 'desc');
          params.append('limit', String(DISPLAY_LIMIT));
          if (location && String(location).trim()) {
            params.append('location', String(location).trim());
          }
          return params;
        };

        // Prefer workers available today; fall back to top-rated if none match.
        let response = await axios.get(`${Api.workerSearch}?${buildParams(true).toString()}`);
        let list = Array.isArray(response.data?.workers) ? response.data.workers : [];

        if (list.filter(isValidWorker).length === 0) {
          response = await axios.get(`${Api.workerSearch}?${buildParams(false).toString()}`);
          list = Array.isArray(response.data?.workers) ? response.data.workers : [];
        }

        if (!cancelled) {
          setWorkers(list.filter(isValidWorker).slice(0, DISPLAY_LIMIT).map(mapWorkerToCard));
        }
      } catch (error) {
        console.error('Failed to load available workers:', error);
        if (!cancelled) setWorkers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWorkers();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const goToWorker = (worker) => {
    if (!worker?.id) return;
    navigate(`/workers/${worker.id}`);
  };

  // Hide the section when there is nothing to show (keeps Home clean).
  if (!loading && workers.length === 0) {
    return null;
  }

  return (
    <Box sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 2.5, md: 3 }, bgcolor: COLORS.sectionBg }}>
      <Container maxWidth="lg">
        {/* Eyebrow — matches Featured / Most Popular flanking lines */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 0.75 }}>
          <Box sx={{ flex: 1, maxWidth: 100, height: '1px', bgcolor: COLORS.border }} />
          <Typography
            sx={{
              fontSize: { xs: '0.6rem', md: '0.65rem' },
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: COLORS.eyebrow,
              whiteSpace: 'nowrap',
            }}
          >
            Hire Instantly
          </Typography>
          <Box sx={{ flex: 1, maxWidth: 100, height: '1px', bgcolor: COLORS.border }} />
        </Box>

        {/* Heading */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: COLORS.heading,
            textAlign: 'center',
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.9rem' },
            letterSpacing: '-0.01em',
            mb: { xs: 2, md: 2.5 },
          }}
        >
          Available Workers Right Now
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: COLORS.accent }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              overflowX: 'auto',
              gap: { xs: '12px', md: '16px' },
              pb: 1,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: COLORS.scrollbar,
                borderRadius: 2,
                '&:hover': { background: COLORS.scrollbarHover },
              },
            }}
          >
            {workers.map((worker) => {
              const isNow = worker.availability === 'now';
              const initials = worker.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              return (
                <Box
                  key={worker.id}
                  sx={{
                    flexShrink: 0,
                    width: { xs: 168, sm: 185, md: CARD_W },
                    display: 'flex',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      '& .aw-card': { boxShadow: `0 10px 28px ${COLORS.shadowHover}` },
                    },
                  }}
                >
                  <Box
                    className="aw-card"
                    sx={{
                      bgcolor: COLORS.cardBg,
                      borderRadius: '16px',
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: `0 3px 12px ${COLORS.shadow}`,
                      transition: 'box-shadow 0.2s ease',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      px: '12px',
                      pt: '14px',
                      pb: '12px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* Photo + availability badge */}
                    <Box sx={{ position: 'relative', mb: 1.25 }}>
                      <Avatar
                        src={worker.photo || undefined}
                        alt={worker.name}
                        imgProps={{ loading: 'lazy', decoding: 'async' }}
                        sx={{
                          width: { xs: 56, md: 64 },
                          height: { xs: 56, md: 64 },
                          bgcolor: COLORS.imageBg,
                          color: COLORS.label,
                          fontWeight: 700,
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          border: `2px solid ${COLORS.border}`,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: -4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          whiteSpace: 'nowrap',
                          bgcolor: isNow ? COLORS.badgeNowBg : COLORS.badgeTodayBg,
                          color: isNow ? COLORS.badgeNowText : COLORS.badgeTodayText,
                          borderRadius: '8px',
                          px: '6px',
                          py: '2px',
                          fontSize: { xs: '0.5rem', md: '0.55rem' },
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          border: `1px solid ${isNow ? 'rgba(46,125,50,0.2)' : 'rgba(230,81,0,0.2)'}`,
                        }}
                      >
                        {isNow ? 'Available Now' : 'Available Today'}
                      </Box>
                    </Box>

                    {/* Name */}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '0.78rem', md: '0.85rem' },
                        color: COLORS.name,
                        textAlign: 'center',
                        lineHeight: 1.25,
                        mt: 0.75,
                        mb: 0.25,
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {worker.name}
                    </Typography>

                    {/* Skills (all) */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                        mb: 0.5,
                        width: '100%',
                        minHeight: '1.4em',
                      }}
                    >
                      {worker.skills.map((skillName, index) => (
                        <Box
                          key={`${worker.id}-${skillName}-${index}`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontWeight: 600,
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            color: COLORS.accent,
                            letterSpacing: '0.02em',
                            bgcolor: 'rgba(201,123,58,0.08)',
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '10px',
                            px: '6px',
                            py: '2px',
                            maxWidth: '100%',
                            lineHeight: 1.3,
                          }}
                        >
                          <SkillLabel name={skillName} />
                        </Box>
                      ))}
                    </Box>

                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                      <Rating
                        value={worker.rating}
                        precision={0.1}
                        readOnly
                        size="small"
                        sx={{
                          fontSize: { xs: '0.8rem', md: '0.9rem' },
                          color: COLORS.accent,
                          '& .MuiRating-iconEmpty': { color: COLORS.border },
                        }}
                      />
                      <Typography sx={{ fontSize: { xs: '0.62rem', md: '0.68rem' }, color: COLORS.desc, fontWeight: 600 }}>
                        {worker.rating.toFixed(1)}
                      </Typography>
                    </Box>

                    {/* Meta: experience + location */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.35,
                        mb: 1,
                        width: '100%',
                      }}
                    >
                      {worker.experience && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <WorkOutline sx={{ fontSize: 12, color: COLORS.subtitle }} />
                          <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.65rem' }, color: COLORS.desc }}>
                            {worker.experience} exp.
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <LocationOn sx={{ fontSize: 12, color: COLORS.subtitle }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            color: COLORS.desc,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 140,
                          }}
                        >
                          {worker.location}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Price */}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.78rem', md: '0.85rem' },
                        color: COLORS.label,
                        mb: 1.25,
                      }}
                    >
                      {formatPrice(worker.price, worker.priceUnit)}
                    </Typography>

                    {/* Actions */}
                    <Box sx={{ mt: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => goToWorker(worker)}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'transparent',
                          color: COLORS.outlineText,
                          border: `1.5px solid ${COLORS.outlineBorder}`,
                          borderRadius: '10px',
                          px: '10px',
                          py: '6px',
                          fontSize: { xs: '0.58rem', md: '0.65rem' },
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'background 0.18s ease, transform 0.15s ease',
                          '&:hover': {
                            bgcolor: COLORS.outlineHoverBg,
                            transform: 'scale(1.02)',
                          },
                        }}
                      >
                        View Profile
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => goToWorker(worker)}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: COLORS.accent,
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '10px',
                          px: '10px',
                          py: '7px',
                          fontSize: { xs: '0.58rem', md: '0.65rem' },
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'background 0.18s ease, transform 0.15s ease',
                          '&:hover': {
                            bgcolor: COLORS.accentHover,
                            transform: 'scale(1.03)',
                          },
                        }}
                      >
                        <TranslatedText>Book Now</TranslatedText>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default React.memo(AvailableWorkersSection);
